<template>
  <div class="two-factor-dialog-wrapper" :class="{'two-factor-dialog-wrapper-popup': isPopup}">
    <div class="two-factor-dialog">
      <v-container style="max-width: 1264px">
        <v-btn
          v-if="!isPopup"
          text
          color="primary"
          @click="handleDialogClose"
        >
          <v-icon
            :size="16"
            class="mr-1"
          >
            mdi-arrow-left
          </v-icon>
          {{ $t('common.back') }}
        </v-btn>

        <div class="dialog-title">
          {{ $t('twoFactor.title') }}

          <div v-if="isPopup" style="margin-top: -10px;">
            <a
              :href="howWillThisProtectUrl" target="_blank"
              style="font-size: 16px"
            >
              {{ $t('twoFactor.howWillThisProtect') }}
            </a>
          </div>
        </div>

        <v-row class="dialog-cards-wrapper">
          <v-col
            cols="12"
            md="6"
            lg="4"
          >
            <v-card class="dialog-card" elevation="0">
              <div class="card-step">
                {{ $t('common.step1') }}
              </div>

              <div class="step-title">
                {{ $t('twoFactor.getApp') }}
              </div>

              <div class="description-text download-apps">
                {{ $t('twoFactor.downloadApps') }}
              </div>

              <div class="download-apps-wrapper">
                <div
                  v-for="(app, index) in apps"
                  :key="index"
                  class="download-app"
                >
                  <div class="download-app-name">
                    {{ app.name }}&nbsp;
                  </div>

                  <div class="download-app-links">
                    (<a :href="app.androidLink" target="_blank">Android</a>, <a :href="app.iosLink" target="_blank">iOS</a>)
                  </div>
                </div>
              </div>

              <div class="description-text download-apps">
                {{ $t('twoFactor.downloadAppsDesc') }}
              </div>
            </v-card>
          </v-col>

          <v-col
            cols="12"
            md="6"
            lg="4"
          >
            <v-card class="dialog-card" elevation="0">
              <div class="card-step">
                {{ $t('common.step2') }}
              </div>

              <div class="step-title">
                {{ $t('twoFactor.scarQR') }}
              </div>

              <div class="qr-code-wrapper">
                <v-progress-circular
                  v-if="loading"
                  :size="50"
                  indeterminate
                  color="primary"
                ></v-progress-circular>

                <div
                  v-else
                  class="qr-code"
                  v-html="twoFactorQrCode"
                ></div>
              </div>

              <div class="secret-key-wrapper">
                <div class="secret-key-title">
                  {{ $t('twoFactor.enterSecretKey') }}
                </div>

                <v-progress-circular
                  v-if="loading"
                  :size="30"
                  indeterminate
                  color="primary"
                  style="margin-top: 30px"
                ></v-progress-circular>

                <div
                  v-else
                  class="secret-key"
                >
                  {{ twoFactorSecretKey }}
                </div>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" lg="4">
            <v-card class="dialog-card enter-digits-wrapper" elevation="0">
              <div>
                <div class="card-step">
                  {{ $t('common.step3') }}
                </div>

                <div class="step-title">
                  {{ $t('twoFactor.enterCode') }}
                </div>

                <div class="enter-digits">
                  {{ $t('twoFactor.enterDigits') }}
                </div>
              </div>

              <div class="enter-code-wrapper">
                <div class="enter-code-title">
                  {{ $t('twoFactor.enterDigitsShort') }}
                </div>

                <div style="margin-top: 14px">
                  <v-text-field
                    v-model="code"
                    rounded
                    dense
                    outlined
                    :label="$t('twoFactor.enterDigitsShort')"
                    :hide-details="!error"
                    :error-messages="$t(`${error}`)"
                    @keydown.enter="handleEnable"
                  />
                </div>

                <v-btn
                  :disabled="code.length !== 6"
                  class="enable-button"
                  color="primary"
                  width="100%"
                  height="44px"
                  :style="error ? 'margin-top: 0' : 'margin-top: 12px'"
                  rounded
                  :loading="loadingConfirm"
                  @click="handleEnable"
                >
                  {{ $t('common.enable') }}
                </v-btn>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <div class="later-button">
          <v-btn
            v-if="isPopup"
            outlined
            rounded
            color="primary"
            large
            @click="handlePopupClose"
          >
            {{ $t('common.later') }}
          </v-btn>
        </div>
      </v-container>
    </div>

    <dialog-two-factor-auth-backup-codes v-if="dialogs.twoFactorAuthBackupCodesDialog" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import DialogTwoFactorAuthBackupCodes from "./DialogTwoFactorAuthBackupCodes";

export default {
  name: 'TwoFactorAuthWrapper',

  components: {
    DialogTwoFactorAuthBackupCodes
  },

  props: {
    isPopup: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      apps: [
        {
          name: 'Google Authenticator',
          androidLink: 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2',
          iosLink: 'https://apps.apple.com/us/app/google-authenticator/id388497605'
        },
        {
          name: 'Duo Mobile',
          androidLink: 'https://play.google.com/store/apps/details?id=com.duosecurity.duomobile&hl=en_US',
          iosLink: 'https://apps.apple.com/us/app/duo-mobile/id422663827'
        },
        {
          name: 'Microsoft Authenticator',
          androidLink: 'https://play.google.com/store/apps/details?id=com.azure.authenticator&referrer=adjust_reftag%3DcKNKUQrsWqbvm',
          iosLink: 'https://apps.apple.com/app/microsoft-authenticator/id983156458'
        }
      ],
      qrCode: '', // should be svg string
      secretKey: '',
      code: '',
      error: '',
      loading: false,
      loadingConfirm: false
    }
  },

  computed: {
    ...mapGetters({
      dialogs: 'main/dialogs',
      twoFactorChangeDevice: 'main/twoFactorChangeDevice',
      twoFactorQrCode: 'main/twoFactorQrCode',
      twoFactorSecretKey: 'main/twoFactorSecretKey'
    }),

    howWillThisProtectUrl () {
      if (this.$i18n.locale === 'ru') {
        return 'https://dolphin-anty.net/blog/2fa-what-it-is-and-why-it-is-needed/?utm_source=ecosystem&utm_medium=cpc&utm_campaign=anty.dolphin.ru.com&utm_content=2fa_hint'
      } else {
        return 'https://dolphin-anty.net/blog/en/two-factor-authentication-2fa-what-it-is-and-why-you-need-it/?utm_source=ecosystem&utm_medium=cpc&utm_campaign=anty.dolphin.ru.com&utm_content=2fa_hint'
      }
    }
  },

  async created () {
    if (!this.twoFactorChangeDevice) {
      this.loading = true

      try {
        const codeData = await this.api.put('two-factory/enable')

        if (codeData.status === 200) {
          this.$store.dispatch('main/setTwoFactorQrCode', codeData.data['qr-code'])
          this.$store.dispatch('main/setTwoFactorSecretKey', codeData.data.secretKey)
        }
      } catch (error) {
        console.log(`Error occurred on getting QR code and Secret key - ${error}`)
      } finally {
        this.loading = false
      }
    }
  },

  methods: {
    handlePopupClose () {
      this.$store.dispatch('main/closeDialog', 'popTwoFactor')
      this.$store.dispatch('main/closeDialog', 'twoFactorAuthBackupCodesDialog')
    },

    handleDialogClose () {
      this.$store.dispatch('main/closeDialog', 'enableTwoFactorAuthDialog')
      this.$store.dispatch('main/closeDialog', 'twoFactorAuthBackupCodesDialog')
    },

    async handleEnable () {
      this.loadingConfirm = true

      try {
        if (this.twoFactorChangeDevice) { // send request to change device, otherwise just send request to confirm code
          const changeDeviceResponse = await this.api.post('two-factory/confirm-change-device', {
            code: this.code
          })

          if (changeDeviceResponse.status === 200) {
            this.$store.dispatch('main/setTwoFactorBackupCodes', changeDeviceResponse.data)

            this.$store.dispatch('main/alert', {
              color: '#6F6F6F',
              message: this.$t('twoFactor.deviceChanged')
            })

            this.$store.dispatch('main/setTwoFactorChangeDevice', false)
            this.$store.dispatch('main/openDialog', 'twoFactorAuthBackupCodesDialog')
          }
        } else {
          const confirmCodeResponse = await this.api.post('two-factory/confirm', {
            code: this.code
          })

          if (confirmCodeResponse.status === 200) {
            this.$store.dispatch('main/setTwoFactorBackupCodes', confirmCodeResponse.data)

            this.$store.dispatch('main/openDialog', 'twoFactorAuthBackupCodesDialog')
          }

          this.$store.dispatch('main/updateProfileLocally', {
            twoFactorEnableDisable: true
          })
        }
      } catch (error) {
        this.error = 'twoFactor.invalidCode'
      } finally {
        this.loadingConfirm = false
      }
    }
  }
}
</script>

<style>
.two-factor-dialog-wrapper {
    padding-top: 90px;
    display: flex;
    justify-content: center;
}
.two-factor-dialog-wrapper-popup {
    padding-top: 20px;
}

.two-factor-dialog-wrapper .go-back {
    display: flex;
    align-items: center;
    cursor: pointer;
}

.two-factor-dialog-wrapper .back-text {
    text-transform: uppercase;
    color: #2196F3;
    margin-left: 5px;
    font-size: 18px;
}

.two-factor-dialog-wrapper .dialog-title {
    font-style: normal;
    font-weight: 500;
    font-size: 34px;
    line-height: 40px;
    letter-spacing: 0.25px;
    color: #212121;
    margin-top: 12px;
}

.two-factor-dialog-wrapper .dialog-cards-wrapper > div {
    height: auto;
}

.two-factor-dialog-wrapper .dialog-cards-wrapper {
    margin-top: 40px;
}

.two-factor-dialog-wrapper .dialog-card {
    background: #F7F7F7;
    border-radius: 32px;
    padding: 32px;
    height: 100%;
}

.two-factor-dialog-wrapper .enter-digits-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.two-factor-dialog-wrapper .card-step {
    font-style: normal;
    font-weight: 300;
    font-size: 34px;
    line-height: 40px;
    letter-spacing: 0.25px;
    color: #000000;
}

.two-factor-dialog-wrapper .step-title {
    margin-top: 24px;
    font-weight: 500;
    font-size: 20px;
    line-height: 23px;
    letter-spacing: 0.15px;
    color: rgba(0, 0, 0, 0.87);
}

.two-factor-dialog-wrapper .description-text {
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 140%;
    letter-spacing: 0.5px;
    color: rgba(0, 0, 0, 0.7);
}

.two-factor-dialog-wrapper .download-apps {
    margin-top: 16px;
}

.two-factor-dialog-wrapper .download-apps-wrapper {
    margin-top: 16px;
}

.two-factor-dialog-wrapper .download-app {
    display: flex;
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 140%;
    letter-spacing: 0.5px;
    color: rgba(0, 0, 0, 0.7);
}

.two-factor-dialog-wrapper .qr-code-wrapper {
    margin-top: 24px;
}

.two-factor-dialog-wrapper .qr-code {
    width: 134px;
    height: 134px;
}

.two-factor-dialog-wrapper .qr-code svg {
    width: 134px;
    height: 134px;
}

.two-factor-dialog-wrapper .secret-key-wrapper {
    margin-top: 24px;
}

.two-factor-dialog-wrapper .secret-key-title {
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 140%;
    letter-spacing: 0.5px;
    color: rgba(0, 0, 0, 0.7);
}

.two-factor-dialog-wrapper .secret-key {
    margin-top: 8px;
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 140%;
    letter-spacing: 0.5px;
    color: rgba(0, 0, 0, 0.87);
}

.enter-digits {
    margin-top: 16px;
    font-weight: 400;
    font-size: 16px;
    line-height: 140%;
    letter-spacing: 0.5px;
    color: rgba(0, 0, 0, 0.7);
}

.two-factor-dialog-wrapper .enter-code-title {
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    color: rgba(0, 0, 0, 0.87);
}

.two-factor-dialog-wrapper .enable-button {
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 19px;
    letter-spacing: 0.5px;
    color: #FFFFFF;
    text-transform: unset;
}

.theme--light.v-btn.v-btn--disabled.v-btn--has-bg {
    background: #F1D6FF !important;
    box-shadow: 0 0 0 rgba(0, 0, 0, 0.12), 0 0 0 rgba(0, 0, 0, 0.14), 0 0 0 rgba(0, 0, 0, 0.2);
    color: #FFFFFF !important;
}

.later-button {
    margin-top: 12px;
    text-align: center;
}
</style>
